import { useRef, useEffect } from 'react'
import { isEqual } from 'lodash'
import { convertTraits } from '@aytm/app/stats/lib/traits'

import { decodeText } from '@aytm/app/stats/lib/helpers'
import { mapReportSearchQuery, getTagsList, getIsDefaultReportParams } from '@aytm/app/stats/page/comments/lib/utils'
import {
  DEFAULT_QUERY,
  DEFAULT_ORDER_BY,
  DEFAULT_ORDER_DIRECTION,
  DEFAULT_PER_PAGE,
  DEFAULT_PER_PAGE_IMAGE_RESPONSES,
  DEFAULT_PER_PAGE_VIDEO_RESPONSES,
  DEFAULT_SELECTED_QUESTION,
  ORDER_BY_OPTIONS
} from '@aytm/app/stats/page/comments/lib/constants'

const useComments = ({
  dispatch,
  searchStateRef,
  reportParams,
  propsRef,
  responsesOptionsRef,
  survey,
  surveyData,
  sentimentOptions,
  specialMode,
  questionsList,
  isShowSentiments,
  isSpecialsSortingDisabledRef,
  responsesRef,
  reportVirtual,
  reportSchema,
  pageData,
  setSurvey,
  setResponsesOptions,
  onQuestionsListChange,
  onUpdateSchema
}) => {
  const specialModeRef = useRef(null)
  specialModeRef.current = specialMode

  useEffect(() => {
    const traits = convertTraits(surveyData.survey || {})
    const _surveyData = Object.assign({}, surveyData.survey || {}, {
      traits,
      codeBooks: (surveyData.survey || {}).codeBooks || []
    })

    if (!isEqual(survey, _surveyData)) {
      setSurvey(_surveyData)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveyData])

  useEffect(() => {
    // turned off for QA mode
    if (searchStateRef.current.isQAMode) return
    if (reportParams.selectedQuestion && (!questionsList || 1 >= questionsList.length)) return

    const selectedOrderBy = reportParams.orderBy && ORDER_BY_OPTIONS.find(item => item.value == reportParams.orderBy)
    const question =
      reportParams.selectedQuestion && reportParams.selectedQuestion !== DEFAULT_SELECTED_QUESTION
        ? questionsList.find(item => item.value == reportParams.selectedQuestion)
        : null
    const pageSize =
      reportParams.pageSize ||
      ('image-responses' == specialModeRef.current
        ? DEFAULT_PER_PAGE_IMAGE_RESPONSES
        : 'video-responses' == specialModeRef.current
        ? DEFAULT_PER_PAGE_VIDEO_RESPONSES
        : DEFAULT_PER_PAGE)
    const isFailedOrderBy =
      selectedOrderBy &&
      ((isSpecialsSortingDisabledRef.current && !selectedOrderBy.isSimple) ||
        (!isShowSentiments && 'sentiment' == selectedOrderBy.value) ||
        ('similarity' == selectedOrderBy.value && (!question || 'ir' != question?.type)) ||
        ('similarity' == selectedOrderBy.value && 'on' != window.features?.oe_sort_similarity?.status) ||
        (question && 'ir' == question.type && !selectedOrderBy.isSimple))

    const request = {
      query: reportParams.query ? reportParams.query : searchStateRef.current.request.query,
      selectedQuestion: reportParams.selectedQuestion || DEFAULT_SELECTED_QUESTION,
      pageSize,
      orderBy: !reportParams.orderBy || isFailedOrderBy ? DEFAULT_ORDER_BY : reportParams.orderBy,
      orderDirection:
        !reportParams.orderDirection || isFailedOrderBy ? DEFAULT_ORDER_DIRECTION : reportParams.orderDirection,
      currentPage: searchStateRef.current.request.currentPage
    }

    const reportQuery = reportParams.query
      ? reportParams.query
      : reportParams.searchString
      ? [
          {
            value: decodeText(reportParams.searchString, false),
            label: decodeText(reportParams.searchString, false),
            type: 'plain'
          }
        ]
      : DEFAULT_QUERY

    if (!isEqual(searchStateRef.current.request.query, mapReportSearchQuery(reportQuery))) {
      request.query = mapReportSearchQuery(reportQuery)

      dispatch({ type: 'SET_PARAM', request: { name: 'totalPages', value: 0 } })
    } else if (searchStateRef.current.request.pageSize != pageSize) {
      const newTotalPages = Math.ceil(searchStateRef.current.totalResponses / pageSize)

      dispatch({ type: 'SET_PARAM', request: { name: 'totalPages', value: newTotalPages } })
    }

    dispatch({
      type: 'SET_REQUEST',
      request: JSON.parse(JSON.stringify(request))
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportParams, questionsList, isShowSentiments])

  useEffect(() => {
    if (!searchStateRef.current.isProcessing) return

    responsesRef.current = []

    setResponsesOptions({
      ...responsesOptionsRef.current,
      survey,
      reportVirtual,
      reportSchema
    })

    const params = {
      tagsList: getTagsList(survey.tags, { codeBooks: survey.codeBooks }),
      isProcessing: false
    }

    if (searchStateRef.current.isQAMode) {
      if (searchStateRef.current.qaAction) {
        params.qaAction = null
      }
      if (searchStateRef.current.isQALoading) {
        params.isQALoading = false
      }
    }

    dispatch({
      type: 'SET_PARAMS',
      request: {
        value: params
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [survey, reportVirtual, reportSchema])

  useEffect(() => {
    responsesRef.current = []

    responsesOptionsRef.current.pageData = pageData
    setResponsesOptions({ ...responsesOptionsRef.current })
    onUpdateSchema({ cs: pageData }, false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageData])

  useEffect(() => {
    if (searchStateRef.current.isProcessing) return

    setResponsesOptions({
      ...responsesOptionsRef.current
    })

    dispatch({
      type: 'SET_PARAM',
      request: {
        name: 'tagsList',
        value: getTagsList(survey.tags, { codeBooks: survey.codeBooks })
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [survey.tags, survey.codeBooks])

  useEffect(() => {
    if (!propsRef.current.onUpdateParams && !searchStateRef.current.isQAMode) return

    const params = structuredClone(searchStateRef.current.request)
    delete params.currentPage // the page is navigation, not report state - nothing restores it
    propsRef.current.onUpdateParams(
      getIsDefaultReportParams(params, specialMode) ? null : params,
      undefined,
      specialMode
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchStateRef.current.request, specialMode])

  useEffect(() => {
    dispatch({
      type: 'SET_PARAM',
      request: { name: 'sentimentOptions', value: sentimentOptions }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sentimentOptions])

  useEffect(() => {
    if (onQuestionsListChange && 'function' == typeof onQuestionsListChange) {
      onQuestionsListChange(questionsList)
    }
  }, [questionsList, onQuestionsListChange])
}

export default useComments
